import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Dialer.css";
import { useParams } from "react-router-dom";

const FacebookDialer = () => {
  const [apiKey, setApiKey] = useState("");
  const [initialApiKey, setInitialApiKey] = useState("");
  const [groups, setGroups] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [fieldList, setFieldList] = useState([]);
  const [facebookFields, setFacebookFields] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [fieldMapping, setFieldMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [assignmentMode, setAssignmentMode] = useState("pool");
  const [associateList, setAssociateList] = useState([]);
  const [selectedAssociates, setSelectedAssociates] = useState([]);
  const { formId } = useParams();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchInitialSettings = async () => {
      try {
        const response = await axios.get(
          `https://leadbankapi.gensparc.com/api/dialer-settings/${formId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data) {
          const savedApiKey = response.data.api_key || "";
          const savedGroup = response.data.group_id || "";
          const savedStatus = response.data.status || "";
          const savedMapping = response.data.field_mapping || {};
          const savedMode = response.data.assignment_mode || "pool";
          const savedAssociates = response.data.associates || [];

          setApiKey(savedApiKey);
          setInitialApiKey(savedApiKey);
          setFieldMapping(savedMapping);
          setSelectedGroup(savedGroup);
          setSelectedStatus(savedStatus);
          setAssignmentMode(savedMode);
          setSelectedAssociates(savedAssociates);

          if (savedApiKey) {
            const success = await fetchDialerData(savedApiKey);
            if (success) {
              if (savedGroup) await fetchStatusList(savedGroup, savedApiKey);
              if (savedMode === "associate") await fetchAssociateList(savedApiKey);
            }
          }
        }
      } catch (error) {
        console.error("Error loading dialer settings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialSettings();
    fetchFacebookFields();
  }, [formId]);

  const fetchFacebookFields = async () => {
    try {
      const response = await axios.get(`https://leadbankapi.gensparc.com/api/facebook/fields/${formId}`);
      if (Array.isArray(response.data)) {
        setFacebookFields(response.data);
      }
    } catch (error) {
      console.error("Error fetching Facebook fields:", error);
    }
  };

  const fetchDialerData = async (providedApiKey) => {
    try {
      const [groupRes, fieldRes] = await Promise.all([
        axios.get(`https://leadbankapi.gensparc.com/api/fetchGroupList?APIKey=${providedApiKey}`),
        axios.get(`https://leadbankapi.gensparc.com/api/fetchFieldList?APIKey=${providedApiKey}`),
      ]);

      if (groupRes.data.ResponseCode === 1) {
        setGroups(groupRes.data.List);
      }

      if (fieldRes.data.ResponseCode === 1) {
        const fieldsArray = Object.entries(fieldRes.data.FieldList).map(([key, value]) => ({
          id: key,
          name: value,
        }));
        setFieldList(fieldsArray);
      }

      // Fetch associates if current mode is associate
      if (assignmentMode === "associate") {
        await fetchAssociateList(providedApiKey);
      }

      return true;
    } catch (error) {
      console.error("Error fetching Dialer data:", error);
      return false;
    }
  };

  const fetchAssociateList = async (providedApiKey) => {
    try {
      const response = await axios.get(
        `https://leadbankapi.gensparc.com/api/fetchAssociateList?APIKey=${providedApiKey}`
      );
      if (response.data.ResponseCode === 1) {
        setAssociateList(response.data.List || []);
      }
    } catch (error) {
      console.error("Error fetching associates:", error);
    }
  };

  const fetchStatusList = async (groupId, key = apiKey) => {
    try {
      const response = await axios.get(
        `https://leadbankapi.gensparc.com/api/fetchStatusList?APIKey=${key}&GroupId=${groupId}`
      );
      if (response.data.ResponseCode === 1) {
        setStatusList(response.data.List);
      }
    } catch (error) {
      console.error("Error fetching status list:", error);
    }
  };

  const handleGroupChange = async (e) => {
    const groupId = e.target.value;
    setSelectedGroup(groupId);
    if (groupId) {
      await fetchStatusList(groupId);
    }
  };

  const handleMappingChange = (fbField, dialerField) => {
    setFieldMapping((prev) => ({ ...prev, [fbField]: dialerField }));
  };

  const handleAssignmentModeChange = async (e) => {
    const mode = e.target.value;
    setAssignmentMode(mode);
    if (mode === "associate" && apiKey) {
      await fetchAssociateList(apiKey);
    }
  };

  const handleAssociateSelection = (e) => {
    const options = Array.from(e.target.selectedOptions, (option) => option.value);
    setSelectedAssociates(options);
  };

  const saveFacebookDialerSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!apiKey || !selectedGroup || !selectedStatus) {
        alert("Please enter an API Key, select a Group, and choose a Status.");
        return;
      }

      await axios.post(
        `https://leadbankapi.gensparc.com/api/facebook-dialer/${formId}`,
        {
          apiKey,
          groupId: selectedGroup,
          status: selectedStatus,
          fieldMapping,
          assignmentMode,
          associate: assignmentMode === "associate" ? selectedAssociates : [],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Sending associate:", selectedAssociates.map((a) => a.value));

      alert("Settings saved!");
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Save failed.");
    }
  };

  if (loading) {
    return <div className="dialer-container"><p>Loading dialer settings...</p></div>;
  }

  return (
    <div className="dialer-container">
      <h2 className="dialer-title">Facebook to Dialer Field Mapping</h2>

      <label className="dropdown-label">API Key :</label>
      <input
        type="text"
        className="input-field"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
      />
      {apiKey && apiKey !== initialApiKey && (
        <button className="btn fetch-btn" onClick={() => fetchDialerData(apiKey)}>
          Fetch Groups & Fields
        </button>
      )}

      <div className="dropdown-group">
        <label className="dropdown-label">Group List:</label>
        <select className="dropdown" value={selectedGroup} onChange={handleGroupChange}>
          <option value="">Select Group</option>
          {groups.map((group) => (
            <option key={group.GroupId} value={group.GroupId}>
              {group.Name}
            </option>
          ))}
        </select>
      </div>

      <div className="dropdown-group">
        <label className="dropdown-label">Status List:</label>
        <select
          className="dropdown"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="">Select Status</option>
          {statusList.map((status) => (
            <option key={status.Name} value={status.Name}>
              {status.Name}
            </option>
          ))}
        </select>
      </div>

      <div className="dropdown-group">
        <label className="dropdown-label">Assignment Mode:</label>
        <div>
          <label>
            <input
              type="radio"
              name="assignmentMode"
              value="pool"
              checked={assignmentMode === "pool"}
              onChange={handleAssignmentModeChange}
            />
            Pool
          </label>
          <label style={{ marginLeft: "15px" }}>
            <input
              type="radio"
              name="assignmentMode"
              value="associate"
              checked={assignmentMode === "associate"}
              onChange={handleAssignmentModeChange}
            />
            Associate
          </label>
        </div>
      </div>

      {assignmentMode === "associate" && (
  <div className="dropdown-group">
    <label className="dropdown-label">Select Associate(s):</label>
    <div className="checkbox-group">
      {associateList.map((associate) => (
        <label key={associate.Id} style={{ display: "block", marginBottom: "4px" }}>
       <input
  type="checkbox"
  value={String(associate.UserId)}
  checked={selectedAssociates.includes(String(associate.UserId))}
  onChange={(e) => {
    const value = e.target.value;
    if (e.target.checked) {
      // Add the associate Id to the selected list
      setSelectedAssociates((prev) => [...prev, value]);
    } else {
      // Remove the associate Id from the selected list
      setSelectedAssociates((prev) => prev.filter((id) => id !== value));
    }
  }}
/>



          {associate.Name}
        </label>
      ))}
    </div>
  </div>
)}


      <h3>Field Mapping</h3>
      <table className="mapping-table">
        <thead>
          <tr>
            <th>Facebook Field</th>
            <th>Dialer Field</th>
          </tr>
        </thead>
        <tbody>
          {facebookFields.map((fbField, idx) => (
            <tr key={idx}>
              <td>{fbField}</td>
              <td>
                <select
                  className="mapping-dropdown"
                  value={fieldMapping[fbField] || ""}
                  onChange={(e) => handleMappingChange(fbField, e.target.value)}
                >
                  <option value="">Select Dialer Field</option>
                  {fieldList.map((df) => (
                    <option key={df.id} value={df.id}>
                      {df.name}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button className="mapped-save" onClick={saveFacebookDialerSettings}>
        Save Facebook Dialer Settings
      </button>
    </div>
  );
};

export default FacebookDialer;
